/**
 * Firebase Cloud Messaging registration, browser side.
 *
 * Returns a discriminated result rather than throwing so callers can report a
 * precise reason (unsupported browser, denied permission, missing token …)
 * into the existing push telemetry.
 *
 * Permission is NEVER requested here implicitly: `requestPermission` is only
 * true when an explicit user opt-in triggered the flow.
 */
import { FCM_VAPID_KEY, FCM_WEB_CONFIG, fcmSwQuery, isFcmConfigured } from "./fcm-config";

export type FcmFailureReason =
  | "not_configured"
  | "unsupported_browser"
  | "permission_denied"
  | "missing_token"
  | "sw_registration_failed"
  | "token_request_failed";

export type FcmTokenResult =
  | { ok: true; token: string }
  | { ok: false; reason: FcmFailureReason; detail?: string };

const SW_URL = "/firebase-messaging-sw.js";
/** Own scope so the messaging worker can never fight the PWA app-shell worker. */
const SW_SCOPE = "/firebase-cloud-messaging-push-scope/";

/** Console breadcrumb kept in production — push failures are hard to reproduce. */
export function logPushIssue(reason: string, detail?: unknown) {
  console.error(`[push] ${reason}`, detail ?? "");
}

/** Resolve once this specific registration has an activated worker. */
async function waitForActiveWorker(
  registration: ServiceWorkerRegistration,
  timeoutMs = 10_000,
): Promise<void> {
  if (registration.active) return;
  const worker = registration.installing ?? registration.waiting;
  if (!worker) return;
  await new Promise<void>((resolve) => {
    const done = () => {
      worker.removeEventListener("statechange", onChange);
      window.clearTimeout(timer);
      resolve();
    };
    const onChange = () => {
      if (worker.state === "activated" || worker.state === "redundant") done();
    };
    const timer = window.setTimeout(done, timeoutMs);
    worker.addEventListener("statechange", onChange);
    if (worker.state === "activated") done();
  });
}

export async function getFcmRegistrationToken(
  requestPermission: boolean,
): Promise<FcmTokenResult> {
  if (!isFcmConfigured()) {
    logPushIssue("not_configured", "Firebase web push credentials are missing");
    return { ok: false, reason: "not_configured" };
  }

  const { isSupported } = await import("firebase/messaging");
  if (
    typeof window === "undefined" ||
    !("serviceWorker" in navigator) ||
    !("Notification" in window) ||
    !(await isSupported().catch(() => false))
  ) {
    logPushIssue("unsupported_browser");
    return { ok: false, reason: "unsupported_browser" };
  }

  const permission =
    Notification.permission === "granted"
      ? "granted"
      : requestPermission
        ? await Notification.requestPermission().catch(() => "denied" as NotificationPermission)
        : Notification.permission;
  if (permission !== "granted") {
    logPushIssue("permission_denied", permission);
    return { ok: false, reason: "permission_denied", detail: permission };
  }

  let registration: ServiceWorkerRegistration;
  try {
    registration = await navigator.serviceWorker.register(`${SW_URL}?${fcmSwQuery()}`, {
      scope: SW_SCOPE,
    });
    // `navigator.serviceWorker.ready` resolves for the registration that
    // controls the *page* (the PWA shell), not this one. Subscribing against a
    // registration whose worker is still installing throws InvalidStateError,
    // which is the classic "permission granted but no device token" failure.
    await waitForActiveWorker(registration);
  } catch (err) {
    logPushIssue("sw_registration_failed", err);
    return {
      ok: false,
      reason: "sw_registration_failed",
      detail: err instanceof Error ? err.message : String(err),
    };
  }

  try {
    const [{ initializeApp, getApp, getApps }, { getMessaging, getToken }] = await Promise.all([
      import("firebase/app"),
      import("firebase/messaging"),
    ]);
    const app = getApps().length ? getApp() : initializeApp(FCM_WEB_CONFIG);
    const token = await getToken(getMessaging(app), {
      vapidKey: FCM_VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
    if (!token) {
      logPushIssue("missing_token", "Firebase returned an empty registration token");
      return { ok: false, reason: "missing_token" };
    }
    return { ok: true, token };
  } catch (err) {
    logPushIssue("token_request_failed", err);
    return {
      ok: false,
      reason: "token_request_failed",
      detail: err instanceof Error ? err.message : String(err),
    };
  }
}

/** Best-effort local cleanup when the visitor turns notifications off. */
export async function deleteFcmRegistration(): Promise<void> {
  try {
    const [{ getApp, getApps }, { deleteToken, getMessaging, isSupported }] = await Promise.all([
      import("firebase/app"),
      import("firebase/messaging"),
    ]);
    if (!getApps().length || !(await isSupported().catch(() => false))) return;
    await deleteToken(getMessaging(getApp())).catch(() => undefined);
    const reg = await navigator.serviceWorker.getRegistration(SW_SCOPE);
    await reg?.unregister().catch(() => undefined);
  } catch (err) {
    logPushIssue("fcm_cleanup_failed", err);
  }
}
